import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Typography, Card, Button, Row, Col, Space, Popconfirm, Select } from 'antd';
import { PlusOutlined, TrophyOutlined, DeleteOutlined, CopyOutlined } from '@ant-design/icons';
import { getSessions, deleteSession, duplicateSession } from '../../utils/sessionStorage';
import CardImage from '../CardImage';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const { Title, Text } = Typography;

const SORT_OPTIONS = [
  { value: 'date-desc', label: 'Date (newest first)' },
  { value: 'date-asc', label: 'Date (oldest first)' },
  { value: 'placement-asc', label: 'Placement (best first)' },
  { value: 'placement-desc', label: 'Placement (worst first)' },
  { value: 'name-asc', label: 'Name (A–Z)' },
  { value: 'name-desc', label: 'Name (Z–A)' },
];

function formatSessionDate(createdAt) {
  if (!createdAt) return null;
  try {
    const d = new Date(createdAt);
    return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return null;
  }
}

export default function SessionList() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(getSessions());
  const [sessionOptions, setSessionOptions] = useState({ formats: [], session_types: [] });
  const [filterLeader, setFilterLeader] = useState(undefined);
  const [filterFormat, setFilterFormat] = useState(undefined);
  const [filterType, setFilterType] = useState(undefined);
  const [sortBy, setSortBy] = useState('date-desc');
  const refresh = useCallback(() => setSessions(getSessions()), []);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/api/session-options`)
      .then(({ data }) => setSessionOptions({
        formats: data.formats || [],
        session_types: data.session_types || [],
      }))
      .catch(() => setSessionOptions({ formats: [], session_types: [] }));
  }, []);

  const leaderOptions = useMemo(() => {
    const seen = new Set();
    return getSessions()
      .filter((s) => s.myLeaderCardId || s.myLeaderName)
      .map((s) => ({
        value: s.myLeaderCardId || s.myLeaderName,
        label: s.myLeaderName || s.myLeaderCardId || '—',
      }))
      .filter((o) => {
        if (seen.has(o.value)) return false;
        seen.add(o.value);
        return true;
      })
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [sessions]);

  const filteredAndSortedSessions = useMemo(() => {
    let list = [...sessions];
    if (filterLeader != null && filterLeader !== '') {
      list = list.filter((s) => (s.myLeaderCardId || s.myLeaderName) === filterLeader);
    }
    if (filterFormat != null && filterFormat !== '') {
      list = list.filter((s) => (s.format || '') === filterFormat);
    }
    if (filterType != null && filterType !== '') {
      list = list.filter((s) => (s.sessionType || 'tournament') === filterType);
    }
    switch (sortBy) {
      case 'date-desc':
        list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'date-asc':
        list.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
        break;
      case 'placement-asc':
        list.sort((a, b) => {
          const pa = a.finalPlacement != null ? Number(a.finalPlacement) : Infinity;
          const pb = b.finalPlacement != null ? Number(b.finalPlacement) : Infinity;
          return pa - pb;
        });
        break;
      case 'placement-desc':
        list.sort((a, b) => {
          const pa = a.finalPlacement != null ? Number(a.finalPlacement) : -1;
          const pb = b.finalPlacement != null ? Number(b.finalPlacement) : -1;
          return pb - pa;
        });
        break;
      case 'name-asc':
        list.sort((a, b) => (a.tournamentName || '').localeCompare(b.tournamentName || ''));
        break;
      case 'name-desc':
        list.sort((a, b) => (b.tournamentName || '').localeCompare(a.tournamentName || ''));
        break;
      default:
        break;
    }
    return list;
  }, [sessions, filterLeader, filterFormat, filterType, sortBy]);

  const confirmDelete = (sessionId) => {
    deleteSession(sessionId);
    refresh();
  };

  const handleDuplicate = (sessionId) => {
    const copy = duplicateSession(sessionId);
    if (copy) {
      refresh();
    }
  };

  const typeLabel = (value) => sessionOptions.session_types.find((t) => t.value === value)?.label || value;

  return (
    <div className="session-tracker-list">
      <div className="session-tracker-list-header">
        <Title level={4} style={{ margin: 0 }}>
          Sessions
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/session-tracker/new')}>
          New session
        </Button>
      </div>

      {sessions.length > 0 && (
        <div className="session-tracker-filters">
          <Select
            placeholder="Leader"
            allowClear
            style={{ minWidth: 140 }}
            value={filterLeader === undefined ? undefined : filterLeader}
            onChange={(v) => setFilterLeader(v === undefined ? undefined : v)}
            options={leaderOptions}
          />
          <Select
            placeholder="Format"
            allowClear
            style={{ minWidth: 120 }}
            value={filterFormat === undefined ? undefined : filterFormat}
            onChange={(v) => setFilterFormat(v === undefined ? undefined : v)}
            options={sessionOptions.formats.map((f) => ({ value: f, label: f }))}
          />
          <Select
            placeholder="Type"
            allowClear
            style={{ minWidth: 120 }}
            value={filterType === undefined ? undefined : filterType}
            onChange={(v) => setFilterType(v === undefined ? undefined : v)}
            options={sessionOptions.session_types.map((t) => ({ value: t.value, label: t.label }))}
          />
          <Select
            value={sortBy}
            onChange={setSortBy}
            style={{ minWidth: 180 }}
            options={SORT_OPTIONS}
          />
        </div>
      )}

      {filteredAndSortedSessions.length === 0 ? (
        <Card>
          <div className="session-tracker-empty">
            <TrophyOutlined style={{ fontSize: 48, color: '#ccc' }} />
            <Text type="secondary">
              {sessions.length === 0
                ? 'No sessions yet. Create one to start tracking.'
                : 'No sessions match the current filters.'}
            </Text>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/session-tracker/new')} style={{ marginTop: 12 }}>
              {sessions.length === 0 ? 'Create session' : 'New session'}
            </Button>
          </div>
        </Card>
      ) : (
        <Row gutter={[16, 16]} className="session-tracker-list-grid">
          {filteredAndSortedSessions.map((session) => (
            <Col xs={24} sm={24} md={12} lg={12} xl={8} key={session.id}>
              <Card hoverable className="session-tracker-session-card">
                <div className="session-tracker-session-card-content">
                  <div
                    className="session-tracker-session-card-clickable"
                    onClick={() => navigate(`/session-tracker/${session.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(`/session-tracker/${session.id}`); } }}
                  >
                    {session.myLeaderImageUrl && (
                      <div className="session-tracker-leader-thumb">
                        <CardImage
                          src={session.myLeaderImageUrl}
                          alt={session.myLeaderName}
                          wrapperClassName="session-tracker-leader-thumb-inner"
                        />
                      </div>
                    )}
                    <div className="session-tracker-session-card-body">
                      <Text strong>{session.tournamentName}</Text>
                      <br />
                      <Text type="secondary">Leader: {session.myLeaderName || session.myLeaderCardId || '—'}</Text>
                      <br />
                      <Text type="secondary">
                        {formatSessionDate(session.createdAt) && `${formatSessionDate(session.createdAt)} · `}
                        {typeLabel(session.sessionType || 'tournament')}
                        {session.format ? ` · ${session.format}` : ''}
                        {session.sessionType === 'practice'
                          ? ` · ${session.rounds?.length ?? 0} games`
                          : ` · ${session.playerCount ?? 0} players${session.finalPlacement != null ? ` · Place: ${session.finalPlacement}` : ''} · ${session.rounds?.length ?? 0} rounds`}
                      </Text>
                    </div>
                  </div>
                  <div className="session-tracker-card-actions">
                    <Space size="small">
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleDuplicate(session.id)}
                        title="Duplicate session"
                      >
                        Duplicate
                      </Button>
                      <Popconfirm
                        title="Delete this session?"
                        onConfirm={() => confirmDelete(session.id)}
                        okText="Delete"
                        cancelText="Cancel"
                      >
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          title="Delete session"
                        >
                          Delete
                        </Button>
                      </Popconfirm>
                    </Space>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
}
